/**
 * Iter139 — E-Mail-Vorlagen Editor mit Live-Vorschau.
 *
 * Zeigt die 3 Templates (Login-Code, Willkommen, Zahlungserinnerung), erlaubt
 * Betreff+HTML zu editieren, und rendert live in einem sicheren <iframe> mit
 * Beispieldaten.
 */
import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Mail, FileText, Info } from "lucide-react";

export default function EmailTemplatesEditor() {
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [form, setForm] = useState({ subject: "", html_body: "" });
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/email-templates");
      setTemplates(r.data || []);
      if (!activeId && r.data.length > 0) {
        selectTemplate(r.data[0]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Templates konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectTemplate = (t) => {
    setActiveId(t.id);
    setForm({ subject: t.subject, html_body: t.html_body });
    setDirty(false);
    renderPreview(t.subject, t.html_body, t.id);
  };

  const active = useMemo(() => templates.find((t) => t.id === activeId), [templates, activeId]);

  const renderPreview = async (subject, html, tid) => {
    setRendering(true);
    try {
      const r = await apiClient.post("/admin/email-templates/render", {
        subject, html_body: html, template_id: tid,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error("Vorschau fehlgeschlagen");
    } finally {
      setRendering(false);
    }
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    // Debounced Live-Preview
    if (window.__previewTimer) clearTimeout(window.__previewTimer);
    window.__previewTimer = setTimeout(() => {
      renderPreview(
        key === "subject" ? value : form.subject,
        key === "html_body" ? value : form.html_body,
        activeId,
      );
    }, 500);
  };

  const save = async () => {
    if (!activeId) return;
    setSaving(true);
    try {
      await apiClient.put(`/admin/email-templates/${activeId}`, form);
      toast.success("Vorlage gespeichert.");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!activeId) return;
    if (!window.confirm("Diese Vorlage auf den Werksstand zurücksetzen? Deine Änderungen gehen verloren.")) return;
    try {
      await apiClient.post(`/admin/email-templates/${activeId}/reset`);
      toast.success("Vorlage zurückgesetzt.");
      await load();
      // Neu geladenes Template auswählen
      const fresh = (await apiClient.get(`/admin/email-templates/${activeId}`)).data;
      selectTemplate(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <Card className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Vorlagen …
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="email-templates-editor">
      <Card className="p-4 bg-indigo-50 border-indigo-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-indigo-600 mt-0.5" />
          <div className="text-sm text-indigo-900">
            <p className="font-semibold">E-Mail-Vorlagen anpassen</p>
            <p className="text-xs mt-0.5 text-indigo-800">
              Verwende Platzhalter im Format <code className="bg-white px-1 rounded">{"{{variable}}"}</code>.
              Rechts siehst du eine Live-Vorschau mit Beispieldaten. Änderungen greifen sofort für alle künftig versendeten E-Mails.
            </p>
          </div>
        </div>
      </Card>

      {/* Template-Auswahl */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="template-tabs">
        {templates.map((t) => (
          <button
            key={t.id}
            onClick={() => selectTemplate(t)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              activeId === t.id
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={`template-tab-${t.id}`}
          >
            {t.name}
            {t.is_custom && (
              <Badge className="ml-1.5 bg-emerald-100 text-emerald-700 text-[10px] px-1 py-0">
                angepasst
              </Badge>
            )}
          </button>
        ))}
      </div>

      {active && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Editor */}
          <div className="space-y-3">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-600" />
                  <span className="font-semibold text-sm text-slate-900">{active.name}</span>
                </div>
                {dirty && <Badge className="bg-amber-100 text-amber-800">Ungespeichert</Badge>}
              </div>
              <p className="text-xs text-slate-500 mb-3">{active.description}</p>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="tpl-subject">Betreff</Label>
                  <Input
                    id="tpl-subject"
                    value={form.subject}
                    onChange={(e) => updateForm("subject", e.target.value)}
                    data-testid="template-subject"
                  />
                </div>
                <div>
                  <Label htmlFor="tpl-body">HTML-Body</Label>
                  <Textarea
                    id="tpl-body"
                    value={form.html_body}
                    onChange={(e) => updateForm("html_body", e.target.value)}
                    rows={20}
                    className="font-mono text-xs"
                    data-testid="template-html"
                  />
                </div>
              </div>

              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-700 mb-1">Verfügbare Platzhalter:</p>
                <div className="flex flex-wrap gap-1">
                  {active.variables.map((v) => (
                    <code
                      key={v}
                      className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-mono cursor-pointer hover:bg-slate-200"
                      onClick={() => {
                        navigator.clipboard.writeText(`{{${v}}}`);
                        toast.success(`{{${v}}} kopiert`);
                      }}
                      title="Klicken zum Kopieren"
                    >
                      {`{{${v}}}`}
                    </code>
                  ))}
                </div>
              </div>
            </Card>

            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={saving || !dirty} className="bg-indigo-600 hover:bg-indigo-700" data-testid="template-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                Speichern
              </Button>
              <Button variant="outline" onClick={reset} disabled={!active.is_custom} data-testid="template-reset-btn">
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Auf Standard zurücksetzen
              </Button>
            </div>
          </div>

          {/* Live-Vorschau */}
          <div>
            <Card className="p-4 sticky top-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-600" />
                  <span className="font-semibold text-sm text-slate-900">Live-Vorschau</span>
                </div>
                {rendering && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
              </div>
              {preview && (
                <>
                  <div className="mb-2 pb-2 border-b border-slate-200">
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Betreff</div>
                    <div className="text-sm font-medium text-slate-900" data-testid="preview-subject">
                      {preview.subject}
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 overflow-hidden bg-slate-50" style={{ height: 560 }}>
                    <iframe
                      title="E-Mail-Vorschau"
                      srcDoc={preview.html}
                      sandbox=""
                      style={{ width: "100%", height: "100%", border: 0, background: "white" }}
                      data-testid="preview-iframe"
                    />
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
